import type { FunctionComponent } from "react";
import { useEffect } from "react";

import { theme } from "shared/lib/styles";

import { AdSlot } from "scmp-app/components/advertisement/ad-slots/ad-slot";
import { TargetingPaidType } from "scmp-app/components/advertisement/ad-slots/types";
import { useMynewsFeedViewContext } from "scmp-app/components/my-news/feed-view/context";
import { MyBookmarks } from "scmp-app/components/my-news/my-bookmarks";
import { MynewsCompleteTypeImp } from "scmp-app/components/my-news/mynews-complete-type-imp";
import { LoadingImage } from "scmp-app/components/my-news/mynews-loading/styles";
import { TrendingTopics } from "scmp-app/components/my-news/trending-topics";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import { useInfiniteScrollTriggerReference } from "scmp-app/lib/hooks";

import { FeedViewArticle } from "./feed-view-article";
import {
  useFeedViewArticles,
  useFeedViewArticlesPagination,
  useFeedViewAuthorPagination,
  useFeedViewEntityData,
  useFeedViewSectionPagination,
  useFeedViewTopicPagination,
} from "./hooks";
import {
  AdsSlotContainer,
  ArticleList,
  Container,
  DesktopContainer,
  MobileAdsContainer,
  MobileContainer,
  SecondaryAdsSlotContainer,
  TabletAdsContainer,
} from "./styles";

export const FeedViewArticles: FunctionComponent = () => {
  const { selectedItem } = useMynewsFeedViewContext();
  const { recommendedArticleIds } = useFeedViewArticles();

  const aiPagination = useFeedViewArticlesPagination(recommendedArticleIds);

  const entityData = useFeedViewEntityData(selectedItem);
  const authorPagination = useFeedViewAuthorPagination(entityData.author);
  const sectionPagination = useFeedViewSectionPagination(entityData.section);
  const topicPagination = useFeedViewTopicPagination(entityData.topic);

  let pagination;
  switch (selectedItem.__typename) {
    case "all":
      pagination = aiPagination;
      break;
    case "Author":
      pagination = authorPagination;
      break;
    case "Section":
      pagination = sectionPagination;
      break;
    default:
      pagination = topicPagination;
      break;
  }

  const { data, hasNext, isLoading, loadNext } = pagination;

  const { infiniteScrollTriggerReference } = useInfiniteScrollTriggerReference({
    hasNextPage: hasNext,
    isLoading,
    onLoadMore: () => loadNext(20),
  });

  const adZone = "mynews/home";

  const handleArticleClick = (entityId: string) => {
    sendGA4Tracking({
      action: "click",
      category: "mynews",
      customized_parameters: {
        article_id: entityId,
        section_type: "topic",
      },
      subcategory: "topic",
    });
  };

  const sendArticleCompletedImpressionList = new Set([2, 7, 12, 17, 22, 27, 32, 37, 42, 44]);

  useEffect(() => {
    if (selectedItem.__typename === "all" && recommendedArticleIds.length > 0) {
      void loadNext(20);
    }
  }, [recommendedArticleIds, loadNext, selectedItem.__typename]);

  return (
    <Container>
      {data && data.length > 0 ? (
        <ArticleList>
          {data?.map((article, index) => (
            <>
              {index === 4 && (
                <MobileContainer>
                  <TrendingTopics />
                  <MobileAdsContainer>
                    <AdsSlotContainer>
                      <AdSlot
                        adUnit="m_banner1"
                        breakpoint={theme.breakpoints.down("desktop")}
                        sizes={[[300, 50], [320, 50], [300, 100], [320, 100], [300, 250], "fluid"]}
                        stickySpacing={20}
                        targeting={{
                          paid: TargetingPaidType.Free,
                        }}
                        withBackground
                        withLabel
                        withSticky
                        zone={adZone}
                      />
                    </AdsSlotContainer>
                  </MobileAdsContainer>
                </MobileContainer>
              )}
              {index === 8 && (
                <MobileContainer>
                  <MyBookmarks />
                  <TabletAdsContainer>
                    <AdsSlotContainer>
                      <AdSlot
                        adUnit="m_lrec1"
                        breakpoint={theme.breakpoints.only("tablet")}
                        sizes={[[320, 250], "fluid"]}
                        stickySpacing={20}
                        targeting={{
                          paid: TargetingPaidType.Free,
                        }}
                        withBackground
                        withLabel
                        withSticky
                        zone={adZone}
                      />
                    </AdsSlotContainer>
                  </TabletAdsContainer>
                </MobileContainer>
              )}
              <FeedViewArticle
                key={index}
                onClick={entityId => {
                  handleArticleClick(entityId!);
                }}
                reference={article}
              />
              {sendArticleCompletedImpressionList.has(index) && (
                <MynewsCompleteTypeImp
                  complete_type="topic"
                  index={index}
                  name={selectedItem.name}
                />
              )}
            </>
          ))}

          {hasNext && (
            <div ref={infiniteScrollTriggerReference}>
              <LoadingImage src="https://assets.i-scmp.com/static/img/gifs/wheel_on_E5E5E5.gif" />
            </div>
          )}
        </ArticleList>
      ) : (
        <div>
          <LoadingImage src="https://assets.i-scmp.com/static/img/gifs/wheel_on_E5E5E5.gif" />
        </div>
      )}
      <DesktopContainer>
        <AdsSlotContainer>
          <AdSlot
            adUnit="d_lrec1"
            breakpoint={theme.breakpoints.up("desktop")}
            sizes={[[320, 250], [300, 250], [300, 600], "fluid"]}
            stickySpacing={20}
            targeting={{
              paid: TargetingPaidType.Free,
            }}
            withBackground
            withLabel
            withSticky
            zone={adZone}
          />
        </AdsSlotContainer>
        <TrendingTopics />
        <MyBookmarks />
      </DesktopContainer>
      <SecondaryAdsSlotContainer>
        <AdSlot
          adUnit="d_lrec2"
          breakpoint={theme.breakpoints.up("desktop")}
          sizes={[[300, 250], [300, 600], [320, 480], "fluid"]}
          stickySpacing={20}
          targeting={{
            paid: TargetingPaidType.Free,
          }}
          withBackground
          withLabel
          withSticky
          zone={adZone}
        />
      </SecondaryAdsSlotContainer>
    </Container>
  );
};

FeedViewArticles.displayName = "FeedViewArticles";
